/**
 * Mouth morph ownership + explosion guards (29 Aug 2026).
 *
 * Several writers can touch the same smile / jaw blendshapes:
 *   1. Speech viseme RAF (exclusive while speaking)
 *   2. Mood overlay (moodAnimator) — one exclusive slot per corner
 *   3. Voice non-speech listening/thinking overlays
 *   4. Procedural idle noise (Avatar.tsx useFrame)
 *   5. Mixer idle-micro / breathing clips
 *
 * There is no single coordinator. These helpers make the writers
 * composable instead of additive-to-infinity:
 *   - One smile morph per corner (Left vs _L vs *old must not stack).
 *   - Overlay writes subtract the previous overlay then add the next.
 *   - Non-speech frames clamp smile/jaw to a safe band so idle/thinking/
 *     startup cannot ratchet into a face-breaking grin.
 *
 * Speech visemes skip the non-speech clamp — they need the full range.
 */

export const MOUTH_SMILE_MORPH_NAMES: readonly string[] = [
  'mouthSmile',
  'mouthSmileLeft',
  'mouthSmileRight',
  'mouthSmile_L',
  'mouthSmile_R',
  'mouthSmileLeftold',
  'mouthSmileRightold',
];

export const MOUTH_OPEN_MORPH_NAMES: readonly string[] = [
  'jawOpen',
  'mouthOpen',
];

/** Idle / thinking / startup — visible warmth, below the teeth-baring band. */
export const SAFE_NONSPEECH_SMILE_MAX = 0.22;

/** Idle jaw must stay a rest-breath, not an open mouth. */
export const SAFE_NONSPEECH_JAW_MAX = 0.08;

const LEFT_SMILE_CANDIDATES = ['mouthSmileLeft', 'mouthSmile_L', 'mouthSmileLeftold'] as const;
const RIGHT_SMILE_CANDIDATES = ['mouthSmileRight', 'mouthSmile_R', 'mouthSmileRightold'] as const;

export function isSmileMorphName(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.includes('mouthsmile');
}

export function isMouthOpenMorphName(name: string): boolean {
  const lower = name.toLowerCase();
  return lower.includes('jawopen') || lower === 'mouthopen';
}

function firstDictIndex(
  dict: Record<string, number>,
  candidates: readonly string[],
): number | undefined {
  const seen = new Set<number>();
  for (const name of candidates) {
    const idx = dict[name];
    if (typeof idx !== 'number') continue;
    if (seen.has(idx)) continue;
    seen.add(idx);
    return idx;
  }
  return undefined;
}

/**
 * One influence index per corner. Prefer live ARKit names, then
 * underscore aliases, then `*old` fallbacks. Models that expose
 * BOTH `mouthSmileLeft` and `mouthSmile_L` as distinct morphs must
 * not receive the same weight on both — that is the face-split grin.
 */
export function pickExclusiveSmileSlots(
  dict: Record<string, number> | null | undefined,
): { left?: number; right?: number } {
  if (!dict) return {};
  const left = firstDictIndex(dict, LEFT_SMILE_CANDIDATES);
  const right = firstDictIndex(dict, RIGHT_SMILE_CANDIDATES);
  const slots: { left?: number; right?: number } = {};
  if (left !== undefined) slots.left = left;
  if (right !== undefined && right !== left) slots.right = right;
  if (slots.left === undefined && dict.mouthSmile !== undefined) {
    slots.left = dict.mouthSmile;
  }
  return slots;
}

export function smileWeightFromBlendshapes(blendshapes: Record<string, number>): number {
  let max = 0;
  for (const name of MOUTH_SMILE_MORPH_NAMES) {
    const value = Number(blendshapes[name]) || 0;
    if (value > max) max = value;
  }
  return max;
}

export function jawWeightFromBlendshapes(blendshapes: Record<string, number>): number {
  return Math.max(
    Number(blendshapes.jawOpen) || 0,
    Number(blendshapes.mouthOpen) || 0,
  );
}

export function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

/**
 * Subtract-then-add overlay write. Callers keep `applied` across frames
 * so a new overlay replaces the old one instead of stacking on it.
 */
export function applyTrackedOverlay(
  influences: number[],
  idx: number,
  nextOverlay: number,
  applied: Map<number, number>,
): void {
  if (idx < 0 || idx >= influences.length) return;
  const previous = applied.get(idx) ?? 0;
  const next = clamp01(nextOverlay);
  influences[idx] = clamp01((influences[idx] ?? 0) - previous + next);
  if (next < 0.001) {
    applied.delete(idx);
  } else {
    applied.set(idx, next);
  }
}

export type MouthFamilyLimits = {
  maxSmile: number;
  maxJaw: number;
};

/**
 * Hard ceiling on smile / jaw-open families. Applied after idle + mood
 * writers on non-speech frames. Does not invent a mood — it only stops
 * stacked writers from exceeding the highest intentional weight.
 */
export function clampMouthFamilyInfluences(
  influences: number[] | undefined,
  dict: Record<string, number> | null | undefined,
  limits: MouthFamilyLimits,
): void {
  if (!influences || !dict) return;
  const smileCap = clamp01(limits.maxSmile);
  const jawCap = clamp01(limits.maxJaw);
  const smileSlots = pickExclusiveSmileSlots(dict);
  for (const idx of [smileSlots.left, smileSlots.right]) {
    if (idx === undefined || idx >= influences.length) continue;
    if ((influences[idx] ?? 0) > smileCap) {
      influences[idx] = smileCap;
    }
  }
  // Also pin leftover alias indices (the ones we refused to drive) to 0
  // so a previous writer cannot leave a stacked grin behind.
  for (const name of MOUTH_SMILE_MORPH_NAMES) {
    const idx = dict[name];
    if (typeof idx !== 'number' || idx >= influences.length) continue;
    if (idx === smileSlots.left || idx === smileSlots.right) continue;
    influences[idx] = 0;
  }
  for (const name of MOUTH_OPEN_MORPH_NAMES) {
    const idx = dict[name];
    if (typeof idx !== 'number' || idx >= influences.length) continue;
    if ((influences[idx] ?? 0) > jawCap) {
      influences[idx] = jawCap;
    }
  }
}

export function maxSmileInfluence(
  influences: number[] | undefined,
  dict: Record<string, number> | null | undefined,
): number {
  if (!influences || !dict) return 0;
  const slots = pickExclusiveSmileSlots(dict);
  return Math.max(
    slots.left !== undefined ? influences[slots.left] ?? 0 : 0,
    slots.right !== undefined ? influences[slots.right] ?? 0 : 0,
  );
}

/**
 * Simulate N frames of idle-noise overlay on a smile morph. Used by tests
 * to prove overlay accounting cannot ratchet past the per-frame delta.
 */
export function simulateIdleNoiseOverlay(
  frames: number,
  sample: (frame: number) => number,
  applied: Map<number, number> = new Map(),
  start = 0,
): { influences: number[]; applied: Map<number, number> } {
  const influences = [start];
  for (let i = 0; i < frames; i++) {
    applyTrackedOverlay(influences, 0, sample(i), applied);
  }
  return { influences, applied };
}
